import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import { CATEGORIES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(`${API}/articles/${slug}`)
      .then((r) => setArticle(r.data))
      .catch(() => setMissing(true));
  }, [slug]);

  const cat = article && CATEGORIES.find((c) => c.id === article.category);

  return (
    <main
      className="min-h-screen bg-samatva-cream text-samatva-ink font-sans"
      data-testid="article-page"
    >
      <div className="noise-overlay" />
      <article className="max-w-3xl mx-auto px-6 py-24 md:py-32">
        <Link
          to="/"
          data-testid="article-back-link"
          className="inline-flex items-center gap-2 text-sm text-samatva-muted hover:text-samatva-plum transition-colors duration-300 mb-12"
        >
          <ArrowLeft size={16} /> Back to Samatva
        </Link>

        {missing && (
          <p className="font-serif text-3xl font-light text-samatva-ink/70">
            This article could not be found.
          </p>
        )}

        {article && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <p
              data-testid="article-category"
              className="text-xs tracking-[0.35em] uppercase text-samatva-plum mb-6"
            >
              {cat ? cat.title : article.category}
              {cat && cat.hindi ? ` · ${cat.hindi}` : ""}
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-light tracking-tight leading-[1.05]">
              {article.title}
            </h1>
            {article.subtitle && (
              <p className="mt-5 font-serif text-xl md:text-2xl italic font-light text-samatva-plum/90 leading-snug">
                {article.subtitle}
              </p>
            )}
            <p className="mt-6 text-xs tracking-[0.25em] uppercase text-samatva-muted">
              {article.author ? `By ${article.author}` : ""}
              {article.author && article.publish_date ? " · " : ""}
              {article.publish_date || ""}
            </p>

            {article.cover_image && (
              <div className="mt-10 overflow-hidden rounded-[2.5rem]">
                <img
                  src={article.cover_image}
                  alt={article.title}
                  className="w-full aspect-[16/9] object-cover"
                />
              </div>
            )}

            <div className="mt-12 space-y-12">
              {article.body
                .split(/\n\n+/)
                .filter(Boolean)
                .map((block, i) =>
                  block.startsWith("## ") ? (
                    <h2
                      key={i}
                      className="font-serif text-2xl md:text-3xl font-medium tracking-tight text-samatva-ink pt-4"
                    >
                      {block.replace(/^##\s+/, "")}
                    </h2>
                  ) : (
                    <p
                      key={i}
                      className="text-base md:text-lg font-light leading-relaxed text-samatva-ink/85"
                    >
                      {block}
                    </p>
                  )
                )}
            </div>

            <div className="mt-20 pt-10 border-t border-samatva-line text-center">
              <p className="font-serif text-2xl font-light text-samatva-ink/80">
                समत्वं योग उच्यते
              </p>
              <Link
                to="/"
                data-testid="article-join-link"
                className="inline-block mt-6 px-8 py-3 rounded-full bg-samatva-ink text-samatva-cream text-sm tracking-wide hover:bg-samatva-plum transition-colors duration-300"
              >
                Join the Samatva circle
              </Link>
            </div>
          </motion.div>
        )}
      </article>
    </main>
  );
}
